import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Pages
import ComingSoon from "@/pages/ComingSoon";
import CustomerPortalShowcase from "@/pages/CustomerPortalShowcase";
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminCompanies from "@/pages/admin/AdminCompanies";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminLocations from "@/pages/admin/AdminLocations";
import AdminOrganismsNew from "@/pages/admin/AdminOrganismsNew";
import AdminChat from "@/pages/admin/AdminChat";
import SchemaBuilder from "@/pages/admin/SchemaBuilder";
import BulkSampleEntryBase from "@/pages/admin/BulkSampleEntry";
import AdminBulkHistory from "@/pages/admin/AdminBulkHistory";
import AdminBulkView from "@/pages/admin/AdminBulkView";
import AdminUserManagement from "@/pages/admin/AdminUserManagement";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminAdministrators from "@/pages/admin/AdminAdministrators";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import PlanktonIdentifier from "@/pages/admin/PlanktonIdentifier";
import SatelliteMonitoring from "@/pages/admin/SatelliteMonitoring";
import ZooplanktonAnalytics from "@/pages/admin/ZooplanktonAnalytics";
import FishHealth from "@/pages/FishHealth";
import AuditLog from "@/pages/admin/AuditLog";
import DeletedDraftView from "@/pages/admin/DeletedDraftView";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerReports from "@/pages/customer/CustomerReports";
import CustomerBulkView from "@/pages/customer/CustomerBulkView";
import CustomerBulkHistory from "@/pages/customer/CustomerBulkHistory";
import OrderSample from "@/pages/customer/OrderSample";
import MyOrders from "@/pages/customer/MyOrders";
import CustomerSettings from "@/pages/customer/CustomerSettings";
import AuthCallback from "@/pages/AuthCallback";
import { ConfirmProvider } from "@/components/ConfirmDialog";

import { Toaster } from "@/components/ui/sonner";
import { TourProvider } from "@/components/OnboardingTour";
import axios from "axios";

import { BACKEND_URL, API } from "@/config/api";

// Simple wrapper - no key tricks needed, reset is handled inside the component
function BulkSampleEntry() {
  return <BulkSampleEntryBase />;
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
      // Clear any old demo user from localStorage (cleanup from old code)
      localStorage.removeItem('demo_user');
    } catch (error) {
      // Clear any stale demo user data
      localStorage.removeItem('demo_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

   
  useEffect(() => {
    checkAuth();
  }, []);

  const logout = async () => {
    // Clear demo user
    localStorage.removeItem('demo_user');
    
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Laster...</p>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
    <AuthContext.Provider value={{ user, setUser, logout, checkAuth }}>
      <TourProvider>
        <ConfirmProvider>
        <div className="App">
          <Toaster position="top-right" />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<ComingSoon />} />
              <Route path="/kundeportal" element={<ComingSoon />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              
              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  user?.role === "admin" ? <AdminDashboard /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  user?.role === "admin" ? <AdminAnalytics /> : <Navigate to="/login" replace />
                }
            />
            <Route
              path="/admin/companies"
              element={
                user?.role === "admin" ? <AdminCompanies /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/samples/bulk"
              element={
                user?.role === "admin" ? <BulkSampleEntry /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/bulk-sample-entry"
              element={
                user?.role === "admin" ? <BulkSampleEntry /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/schema-builder"
              element={
                user?.role === "admin" ? <SchemaBuilder /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/bulk-history"
              element={
                user?.role === "admin" ? <AdminBulkHistory /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/bulk-view/:bulkId"
              element={
                user?.role === "admin" ? <AdminBulkView /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/bulk-edit/:bulkId"
              element={
                user?.role === "admin" ? <BulkSampleEntry /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/users"
              element={
                user?.role === "admin" ? <AdminUserManagement /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/orders"
              element={
                user?.role === "admin" ? <AdminOrders /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/plankton"
              element={
                user?.role === "admin" ? <PlanktonIdentifier /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/satellite-monitoring"
              element={
                user?.role === "admin" ? <SatelliteMonitoring /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/settings"
              element={
                user?.role === "admin" ? <AdminSettings /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/locations"
              element={
                user?.role === "admin" ? <AdminLocations /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/organisms"
              element={
                user?.role === "admin" ? <AdminOrganismsNew /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/thresholds"
              element={
                user?.role === "admin" ? <AdminOrganismsNew /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/zooplankton"
              element={
                user?.role === "admin" ? <ZooplanktonAnalytics /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/administrators"
              element={
                user?.role === "admin" ? <AdminAdministrators /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/security"
              element={
                user?.role === "admin" ? <AdminSecurity /> : <Navigate to="/login" replace />
              }
            />
            
            {/* Fish Health - accessible to both admin and customer */}
            <Route
              path="/admin/fish-health"
              element={
                user?.role === "admin" ? <FishHealth /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                user?.role === "admin" ? <AuditLog /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/chat"
              element={
                user?.role === "admin" ? <AdminChat /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/admin/deleted-draft/:draftId"
              element={
                user?.role === "admin" ? <DeletedDraftView /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/customer/fish-health"
              element={
                user?.role === "customer" ? <FishHealth /> : <Navigate to="/login" replace />
              }
            />
            
            {/* Customer routes */}
            <Route
              path="/dashboard"
              element={
                user?.role === "customer" ? <CustomerDashboard /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/dashboard/reports"
              element={
                user?.role === "customer" ? <CustomerReports /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/dashboard/bulk-history"
              element={
                user?.role === "customer" ? <CustomerBulkHistory /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/dashboard/bulk-view/:bulkId"
              element={
                user?.role === "customer" ? <CustomerBulkView /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/customer/order-sample"
              element={
                user?.role === "customer" ? <OrderSample /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/customer/orders"
              element={
                user?.role === "customer" ? <MyOrders /> : <Navigate to="/login" replace />
              }
            />
            <Route
              path="/customer/settings"
              element={
                user?.role === "customer" ? <CustomerSettings /> : <Navigate to="/login" replace />
              }
            />
          </Routes>
        </BrowserRouter>
        </div>
        </ConfirmProvider>
      </TourProvider>
    </AuthContext.Provider>
    </HelmetProvider>
  );
}

export default App;